import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { GlobalSwal } from '../../styles/swal.style';
import { Error } from '../../models/error.model';
import { Company, CompanyToken } from '../../models/company.model';
import { Contact } from '../../models/contact.model';
import { modalHide } from '../../utils/modal.util';
import { imageCompany } from '../../scripts/image.value.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { getError } from '../../languages/translations/response';
import { formatLocation } from '../../scripts/format.value.script';

import AppLoading from '../Loading';

export interface AppModalContactAddProps {
  companyForUser: CompanyToken | null,
  meCompanies: Company[] | null,
  contact: Contact | null,
  setContact: Dispatch<SetStateAction<Contact | null>>,
  setContactCompanies: Dispatch<SetStateAction<Company[] | null>>
};

let errorResponse: Error, contactResponse: Contact;

const AppModalContactUpdateCompanies: React.FunctionComponent<AppModalContactAddProps> = ({meCompanies, companyForUser, contact, setContact, setContactCompanies}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([])

  const submitContactUpdate = async () => {
    setLoadIndicator('on')

    if (meCompanies && companyForUser && contact && selectedCompanies) {
      await ApiDigitalAdminContact.putContactExtend(companyForUser.id, contact.id, selectedCompanies).then( (response) => {
        if (response.status === 200) {
          contactResponse = response.data

          setContact(contactResponse)
          setContactCompanies(meCompanies.filter((temp_item) => (contactResponse.data.companies.includes(temp_item.id))))

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedContact,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalContactAdd()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeCompanies = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (selectedCompanies.includes(event.target.value)) {
      setSelectedCompanies(selectedCompanies.filter((item) => (item !== event.target.value)))
    } else {
      setSelectedCompanies([...selectedCompanies, event.target.value])
    }
  }

  const executeHideModalContactAdd = () => {
    modalHide('modal-contact-update-companies')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    setSelectedCompanies(contact?.data.companies ?? [])
  }

  useEffect( () => {
    if (contact) {
      setSelectedCompanies(contact.data.companies)
    } else {
      setSelectedCompanies([])
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact])

  return (
    <div id="modal-contact-update-companies" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.addOrRemoveContactCompanies}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && meCompanies
              ?
              <>
                <div className="text-justify text-secondary mb-5">
                  {lang.labels.theCurrentCompanyIs}
                  <strong className="mx-1">{companyForUser.name}</strong>
                  {lang.labels.youWillNotBeAbleToRemoveItFromThisCompany}
                </div>
                <div className="mh-350px hover-scroll-y px-3 border-top border-bottom">
                  { meCompanies.filter(item => item.id !== companyForUser.id).map (( (item, index) => { return (
                    <div key={index}>
                      <label className="d-flex flex-stack cursor-pointer my-3">
                        <span className="d-flex align-items-center me-3">
                          <span className="symbol symbol-50px me-5">
                            <span className="symbol-label bg-light">
                              <img src={imageCompany(item.logo)} alt={lang.labels.logo} className="w-75 h-75" />
                            </span>
                          </span>
                          <span className="d-flex flex-column">
                            <span className="fs-6 fw-bolder">{item.name}</span>
                            <small className="text-muted">{formatLocation(lang, item.address)}</small>
                          </span>
                        </span>
                        <span className="form-check form-check-custom form-check-solid">
                          <input className="form-check-input" type="checkbox" value={item.id} checked={selectedCompanies.includes(item.id)} onChange={handleChangeCompanies} />
                        </span>
                      </label>
                    </div>
                  )}))}
                </div>
              </>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalContactAdd}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitContactUpdate}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalContactUpdateCompanies;
